// Layout for every public marketing page. It owns the site header and footer; the theme
// follows the visitor's OS (dark OS = refined dark, light OS = light pages with dark product UI).
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { SiteHeader } from '@/components/site-header'
import { SiteFooter } from '@/components/site-footer'
import { RelatedPages } from '@/components/related-pages'
import { ViewPill } from '@/components/view-pill'
import { useReveals } from '@/lib/reveal'
import { VIEW_TRANSITION_CSS, markdownHref } from '@/lib/machine-view'
import { requestHost } from '@/lib/request-host'
import { mainSiteUrlForStatusHost } from '@/lib/status-host'

export const Route = createFileRoute('/_site')({
  component: SiteLayout,
  // A marketing page asked for on status.uptrack.app is a duplicate of the uptrack.app page. Send it there
  // with a permanent redirect. The status page itself (/ and /status/*) does not use this layout.
  beforeLoad: ({ location }) => {
    const target = mainSiteUrlForStatusHost(requestHost(), location.pathname, location.searchStr)
    if (target) throw redirect({ href: target, statusCode: 301 })
  },
  // Every page of this layout has a markdown version (OpenSpec 137). The worker converts a page only
  // when it carries this link, so the link is also what marks a page as public.
  // The layout's own match is `/`, so the page's path is the last match.
  head: ({ matches }) => ({
    links: [{ rel: 'alternate', type: 'text/markdown', href: markdownHref(matches[matches.length - 1].pathname) }],
    // Smooth switch to the machine view; only this layout opts in, so the app keeps plain page loads.
    styles: [{ children: VIEW_TRANSITION_CSS }],
  }),
})

function SiteLayout() {
  // Drives the opt-in scroll reveal for every [data-reveal] section below.
  useReveals()

  return (
    <div className="flex min-h-screen flex-col bg-background pb-14 text-foreground">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:border focus:border-border focus:bg-card focus:px-3 focus:py-2 focus:text-sm focus:text-foreground focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>
      <SiteHeader />
      <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
        <Outlet />
        <RelatedPages />
      </main>
      <SiteFooter machineView />
      <ViewPill />
    </div>
  )
}
